import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import { initSentry } from './sentry';

// Common module
import {
  ErrorBoundary,
  ThemeModeProvider,
  useThemeMode,
  DirtyFormProvider,
  ModuleAccessProvider,
  ActivityFeed,
  PageHeader,
  PageContent,
} from '@viilutalous/common';
import NavigationBlocker from './components/NavigationBlocker';

// Local components (depend on auth)
import Layout from './components/Layout';
import TenantSlugGuard from './components/TenantSlugGuard';
import MeLayout from './components/MeLayout';
import { ViiluCommonPackageProviders } from './contexts/ViiluCommonPackageProviders';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { BreadcrumbProvider, useBreadcrumb } from './contexts/BreadcrumbContext';
import { ChatProvider } from './contexts/ChatContext';

// Auth module
import {
  AuthProvider,
  useAuth,
  Login,
  UserSettings,
  GoogleCallback,
  AzureCallback,
} from './modules/auth';

// Invoicing module
import { Invoices, InvoiceForm, InvoiceListReport, Payments, PaymentForm, PaymentTermsConfiguration, PaymentTermForm, PaymentMethodsConfiguration, PaymentMethodForm, PaymentNotifications, TransferInvoices, BulkInvoicing, BulkInvoicingByProduct, BulkInvoicingMetered, WaterBilling } from '@viilutalous/invoicing';

// Contract Invoicing module
import { ContractInvoices, ContractInvoiceForm } from '@viilutalous/contract-invoicing';

// Purchasing module
import { PurchaseInvoices, PurchaseInvoiceForm, Charges, ChargeForm, PurchaseOrders, PurchaseOrderForm, PurchasingSettings, PurchaseInvoiceApprovalRulesSettings, PendingApprovalsPage } from '@viilutalous/purchasing';

// Customers module
import { Contacts, ContactForm, ContactGroups, ContactGroupForm } from '@viilutalous/customers';

// CRM module
import { Activities, ActivityForm, Leads, LeadForm, Deals, DealForm, PipelineSettings } from '@viilutalous/crm';

// Inventory module
import {
  Products, ProductForm, Stocks, StockForm, UnitsConfiguration, UnitForm, ProductGroups, ProductGroupForm,
} from '@viilutalous/inventory';

// Sales module
import { SalesOrders, SalesOrderForm } from '@viilutalous/sales';

// Accounting module
import { Vouchers, VoucherForm, VatConfiguration, VatForm, ChartOfAccountsConfiguration, ChartOfAccountsForm, FinancialYearsConfiguration, FinancialYearForm, VoucherTypesConfiguration, VoucherTypeForm, DimensionsConfiguration, DimensionForm, CurrenciesConfiguration, CurrencyForm, ExchangeRates, ExchangeRateHistory, CoaRoleConfigsConfiguration, CoaRoleConfigForm, CoaGroupsConfiguration, CoaGroupForm, TalousarvioPage, LedgerBrowser, AccountMatching, ChargeFinancingBalances } from '@viilutalous/accounting';

// Reporting pages
import { ReportDesigner } from '@viilutalous/report-designer';
// Accounting: entry type settings
import { EntryTypesConfiguration } from '@viilutalous/accounting';
import { ReportViewer, ReportRunner } from '@viilutalous/reporting';

// Store module
import { StoreProvider } from '@viilutalous/store';
import StoreRegistrar from './components/StoreRegistrar';

// Dashboard module
import {
  BoardDashboard,
  Dashboard,
  ModuleDashboard,
  OpenItemsDashboard,
  OpenItemsWorkQueue,
} from '@viilutalous/dashboard';

// Business Intelligence module
import { BIDashboard } from '@viilutalous/bi';

// Communications module
import { SendingService } from './modules/communications';
import { SiMessages, RiMessages, MaventaSettings } from '@viilutalous/biz-maventa';

// Banking module
import { Statements, BankPayments, CustomerAllocationRebuild, AllocationOverview, EnableBankingConnections, EnableBankingConnection, EnableBankingCallback } from '@viilutalous/banking';
import { CompanySettings, TenantUsers, RoleManagement } from '@viilutalous/company';

// Salaries module
import { SalariesDashboard, SalaryAccountingImport, SalaryPayments } from '@viilutalous/salaries';

// Projects module
import { Projects, ProjectTemplates, ProjectForm, Tasks, TaskForm, MyTasks, TaskStagesMatrix, Tags } from '@viilutalous/projects';

// Time Tracking module
import { TimeTracker, AllTimeEntries, WeekCalendar } from '@viilutalous/time-tracking';

// Helpdesk module (VII-208, VII-209)
import { Manuals, ManualEditor, SupportProvider } from '@viilutalous/helpdesk';

// Knowledgebase module
import { KnowledgebaseList, KnowledgebaseEntry, KnowledgebaseEditor } from '@viilutalous/knowledgebase';

// Webhooks module (VII-211)
import { WebhooksSettings } from '@viilutalous/webhooks';

// Metered billing module (BIZ-597)
import { MeteredBillingPeriod, MeteredBillingPeriods, MeteredBillingSubjectForm, MeteredBillingSubjects } from '@viilutalous/metered-billing';

// Customer support portal (VII-207)
import PortalRoutes from './portal/PortalRoutes';

// Admin module
import { AdminDashboard, HostingCompanies, HostingCompanyForm, Tenants, TenantForm, Users, UserForm, AccessLogs, ActiveSessions, BankingJobQueue, DatabaseImport, VoucherCorrection, PaymentRematch, CloseRemainder, ChargeApCorrection, CoaRoleCorrection } from '@viilutalous/admin';

// Employees module (local pages)
import Employees from './pages/employees/Employees';
import EmployeeForm from './pages/employees/EmployeeForm';
import Departments from './pages/employees/Departments';
import DepartmentForm from './pages/employees/DepartmentForm';

// Expenses module (local pages)
import ExpensesPage from './pages/expenses/Expenses';
import ExpenseForm from './pages/expenses/ExpenseForm';
import ExpenseCategories from './pages/expenses/ExpenseCategories';
import ExpenseCategoryForm from './pages/expenses/ExpenseCategoryForm';

// Local dashboard pages
// SendingServiceDashboard removed - replaced by SendingService from communications module
import TenantRegistration from './pages/TenantRegistration';
import CompanyInfoSetup from './pages/CompanyInfoSetup';
import ExportData from './pages/ExportData';
import PublicInvoiceViewer from './pages/PublicInvoiceViewer';
import UserGroups from './pages/UserGroups';
import MyApprovals from './pages/MyApprovals';
import MyHome from './pages/MyHome';
import MyTasksAcrossTenants from './pages/MyTasks';
import MyCompanies from './pages/MyCompanies';
import MeAllCompanies from './pages/MeAllCompanies';
import { NotFound } from '@viilutalous/common';

// Initialize Sentry
initSentry();

/**
 * `requireSetup={false}` skips the company-info setup gate: the Enable Banking
 * bank return must render its own outcome page even when the tenant's setup is
 * incomplete (the gate renders `<Layout>` whose `<Outlet />` is empty outside
 * the /app route tree, so the customer would see a blank page). My Desktop
 * (`/me/*`) skips it too: it is cross-tenant, and the gate's `<Layout>` would
 * render the /me child without MeLayout's providers.
 */
function PrivateRoute({ children, requireSetup = true }) {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { registrationRequired, registrationEmail, setupRequired, loading: tenantLoading, refreshTenants } = useTenant();

  if (authLoading || tenantLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/app/login" />;
  }

  if (registrationRequired) {
    return <TenantRegistration userEmail={registrationEmail} onSuccess={refreshTenants} />;
  }

  if (setupRequired && requireSetup) {
    return (
      <Layout>
        <CompanyInfoSetup onSuccess={refreshTenants} />
      </Layout>
    );
  }

  return children;
}

/**
 * `/app` (no slug). User just authenticated or hit the bare app root — pick a
 * default tenant slug and redirect to its dashboard. Showing nothing until
 * tenants resolve keeps the address bar from flickering through an invalid URL.
 */
function RootSlugRedirect() {
  const { currentTenant, loading } = useTenant();
  if (loading || !currentTenant?.slug) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }
  return <Navigate to={`/app/${currentTenant.slug}/dashboard`} replace />;
}

function RootRedirect() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Authenticated: go to /app, which RootSlugRedirect resolves to /app/{slug}/dashboard.
  return <Navigate to={isAuthenticated ? "/app" : "/app/login"} replace />;
}

/**
 * Bank return page for Enable Banking. Lives outside `/app/:tenantSlug` because the
 * bank redirects to a single registered URL; the package page finds the tenant from
 * the `state` token and we switch to it before landing on the connection page.
 */
function EnableBankingCallbackPage() {
  const { tenants, selectTenant, loading, registrationRequired } = useTenant();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // TenantContext reports loading=false once before Firebase has confirmed the user
  // (its "not authenticated" branch) and doesn't re-arm it when fetchTenants runs,
  // so "not loading" alone briefly means "no tenants yet". Wait for the list itself;
  // registrationRequired is the one case where it legitimately stays empty.
  const ready = !loading && ((tenants || []).length > 0 || registrationRequired);
  return (
    <ViiluCommonPackageProviders>
      <EnableBankingCallback
        searchParams={searchParams}
        tenants={tenants || []}
        ready={ready}
        onDone={(tenant, connection) => {
          selectTenant(tenant);
          navigate(`/app/${tenant.slug}/bank/enable-banking/${connection.id}`, { replace: true });
        }}
        onBack={(tenant) => navigate(tenant?.slug ? `/app/${tenant.slug}/bank/enable-banking` : '/app', { replace: true })}
      />
    </ViiluCommonPackageProviders>
  );
}

function WrapperHoc(Component) {
  return function WrappedComponent(props) {
    const { currentTenant } = useTenant();
    const { setItemTitle, setParentTitle } = useBreadcrumb();
    const { user } = useAuth();
    return (
      <ViiluCommonPackageProviders>
        <Component
          tenantId={currentTenant?.id}
          setBreadcrumbTitle={setItemTitle}
          setBreadcrumbParentTitle={setParentTitle}
          user={user}
          {...props}
        />
      </ViiluCommonPackageProviders>
    );
  };
}

// Wrapper components that inject common props and contexts
const ModuleDashboardWrapper = WrapperHoc(ModuleDashboard);
const InvoicesWrapper = WrapperHoc(Invoices);
const InvoiceFormWrapper = WrapperHoc(InvoiceForm);
const InvoiceListReportWrapper = WrapperHoc(InvoiceListReport);
const PaymentNotificationsWrapper = WrapperHoc(PaymentNotifications);
const TransferInvoicesWrapper = WrapperHoc(TransferInvoices);
const BulkInvoicingWrapper = WrapperHoc(BulkInvoicing);
const BulkInvoicingByProductWrapper = WrapperHoc(BulkInvoicingByProduct);
const BulkInvoicingMeteredWrapper = WrapperHoc(BulkInvoicingMetered);
const WaterBillingWrapper = WrapperHoc(WaterBilling);
const ContractInvoicesWrapper = WrapperHoc(ContractInvoices);
const ContractInvoiceFormWrapper = WrapperHoc(ContractInvoiceForm);
const PurchaseInvoicesWrapper = WrapperHoc(PurchaseInvoices);
const PurchaseInvoiceFormWrapper = WrapperHoc(PurchaseInvoiceForm);
const ChargesWrapper = WrapperHoc(Charges);
const ChargeFormWrapper = WrapperHoc(ChargeForm);
const PurchaseOrdersWrapper = WrapperHoc(PurchaseOrders);
const PurchaseOrderFormWrapper = WrapperHoc(PurchaseOrderForm);
const PurchasingReportsWrapper = WrapperHoc((props) => <ReportRunner {...props} defaultModule="purchasing" />);
const PurchasingSettingsWrapper = WrapperHoc(PurchasingSettings);
const PurchaseInvoiceApprovalRulesSettingsWrapper = WrapperHoc(PurchaseInvoiceApprovalRulesSettings);
const PendingApprovalsPageWrapper = WrapperHoc(PendingApprovalsPage);
const UserGroupsWrapper = WrapperHoc(UserGroups);
const ContactsWrapper = WrapperHoc(Contacts);
const ContactFormWrapper = WrapperHoc(ContactForm);
const ContactGroupsWrapper = WrapperHoc(ContactGroups);
const ContactGroupFormWrapper = WrapperHoc(ContactGroupForm);
const ActivitiesWrapper = WrapperHoc(Activities);
const ActivityFormWrapper = WrapperHoc(ActivityForm);
const LeadsWrapper = WrapperHoc(Leads);
const LeadFormWrapper = WrapperHoc(LeadForm);
const DealsWrapper = WrapperHoc(Deals);
const DealFormWrapper = WrapperHoc(DealForm);
const PipelineSettingsWrapper = WrapperHoc(PipelineSettings);
const CrmReportsWrapper = WrapperHoc((props) => <ReportRunner {...props} defaultModule="crm" />);
const SalesOrdersWrapper = WrapperHoc(SalesOrders);
const SalesOrderFormWrapper = WrapperHoc(SalesOrderForm);
const SalesReportsWrapper = WrapperHoc((props) => <ReportRunner {...props} defaultModule="sales" />);
const ProductsWrapper = WrapperHoc(Products);
const ProductFormWrapper = WrapperHoc(ProductForm);
const StocksWrapper = WrapperHoc(Stocks);
const StockFormWrapper = WrapperHoc(StockForm);
const UnitsConfigurationWrapper = WrapperHoc(UnitsConfiguration);
const UnitFormWrapper = WrapperHoc(UnitForm);
const ProductGroupsWrapper = WrapperHoc(ProductGroups);
const ProductGroupFormWrapper = WrapperHoc(ProductGroupForm);
const InventoryReportsWrapper = WrapperHoc((props) => <ReportRunner {...props} defaultModule="inventory" />);
const PaymentsWrapper = WrapperHoc(Payments);
const PaymentFormWrapper = WrapperHoc(PaymentForm);
const VouchersWrapper = WrapperHoc(Vouchers);
const TalousarvioPageWrapper = WrapperHoc(TalousarvioPage);
const LedgerBrowserWrapper = WrapperHoc(LedgerBrowser);
const AccountMatchingWrapper = WrapperHoc(AccountMatching);
const VoucherFormWrapper = WrapperHoc(VoucherForm);
const VatConfigurationWrapper = WrapperHoc(VatConfiguration);
const VatFormWrapper = WrapperHoc(VatForm);
const ChartOfAccountsConfigurationWrapper = WrapperHoc(ChartOfAccountsConfiguration);
const ChartOfAccountsFormWrapper = WrapperHoc(ChartOfAccountsForm);
const FinancialYearsConfigurationWrapper = WrapperHoc(FinancialYearsConfiguration);
const FinancialYearFormWrapper = WrapperHoc(FinancialYearForm);
const VoucherTypesConfigurationWrapper = WrapperHoc(VoucherTypesConfiguration);
const VoucherTypeFormWrapper = WrapperHoc(VoucherTypeForm);
const EntryTypesConfigurationWrapper = WrapperHoc(EntryTypesConfiguration);
const DimensionsConfigurationWrapper = WrapperHoc(DimensionsConfiguration);
const ChargeFinancingBalancesWrapper = WrapperHoc(ChargeFinancingBalances);
const DimensionFormWrapper = WrapperHoc(DimensionForm);
const CurrenciesConfigurationWrapper = WrapperHoc(CurrenciesConfiguration);
const ExchangeRatesWrapper = WrapperHoc(ExchangeRates);
const ExchangeRateHistoryWrapper = WrapperHoc(ExchangeRateHistory);
const CurrencyFormWrapper = WrapperHoc(CurrencyForm);
const CoaRoleConfigsConfigurationWrapper = WrapperHoc(CoaRoleConfigsConfiguration);
const CoaRoleConfigFormWrapper = WrapperHoc(CoaRoleConfigForm);
const CoaGroupsConfigurationWrapper = WrapperHoc(CoaGroupsConfiguration);
const CoaGroupFormWrapper = WrapperHoc(CoaGroupForm);
const PaymentTermsConfigurationWrapper = WrapperHoc(PaymentTermsConfiguration);
const PaymentTermFormWrapper = WrapperHoc(PaymentTermForm);
const PaymentMethodsConfigurationWrapper = WrapperHoc(PaymentMethodsConfiguration);
const PaymentMethodFormWrapper = WrapperHoc(PaymentMethodForm);
const InvoiceReportsWrapper = WrapperHoc((props) => <ReportRunner {...props} defaultModule="invoicing" />);
const AccountingReportsWrapper = WrapperHoc((props) => <ReportRunner {...props} defaultModule="accounting" />);
const DashboardWrapper = WrapperHoc(Dashboard);
function ActivityPage({ tenantId }) {
  return (
    <>
      <PageHeader />
      <PageContent>
        <ActivityFeed tenantId={tenantId} />
      </PageContent>
    </>
  );
}
const ActivityWrapper = WrapperHoc(ActivityPage);
const StatementsWrapper = WrapperHoc(Statements);
const BankPaymentsWrapper = WrapperHoc(BankPayments);
const CustomerAllocationRebuildWrapper = WrapperHoc(CustomerAllocationRebuild);
const AllocationOverviewWrapper = WrapperHoc(AllocationOverview);
// Enable Banking pages are gated per tenant by a platform-admin approval, enforced by
// the API; the list page explains when the company is not enabled (BIZ-617).
const EnableBankingConnectionsWrapper = WrapperHoc(EnableBankingConnections);
const EnableBankingConnectionWrapper = WrapperHoc(EnableBankingConnection);
const SalariesDashboardWrapper = WrapperHoc(SalariesDashboard);
const SalaryAccountingImportWrapper = WrapperHoc(SalaryAccountingImport);
const SalaryPaymentsWrapper = WrapperHoc(SalaryPayments);
const SalariesSectionWrapper = WrapperHoc(function SalariesSection(props) {
  const { section } = useParams();
  return <SalariesDashboard {...props} section={section} />;
});
const ProjectsWrapper = WrapperHoc(Projects);
const ProjectTemplatesWrapper = WrapperHoc(ProjectTemplates);
const ProjectFormWrapper = WrapperHoc(ProjectForm);
const TasksWrapper = WrapperHoc(Tasks);
const TaskFormWrapper = WrapperHoc(TaskForm);
// NotFound uses useRouter(); it must render inside ViiluCommonPackageProviders
// (the RouterProvider) like every other page, or the 404 itself throws.
const NotFoundWrapper = WrapperHoc(NotFound);
const MyTasksWrapper = WrapperHoc(MyTasks);
const TaskStagesMatrixWrapper = WrapperHoc(TaskStagesMatrix);
const TagsWrapper = WrapperHoc(Tags);
const TimeTrackerWrapper = WrapperHoc(TimeTracker);
const ManualsWrapper = WrapperHoc(Manuals);
const ManualEditorWrapper = WrapperHoc(ManualEditor);
const KnowledgebaseListWrapper = WrapperHoc(KnowledgebaseList);
const KnowledgebaseEntryWrapper = WrapperHoc(KnowledgebaseEntry);
const KnowledgebaseEditorWrapper = WrapperHoc(KnowledgebaseEditor);
const AllTimeEntriesWrapper = WrapperHoc(AllTimeEntries);
const WeekCalendarWrapper = WrapperHoc(WeekCalendar);
const SendingServiceWrapper = WrapperHoc(SendingService);
const SiMessagesWrapper = WrapperHoc(SiMessages);
const RiMessagesWrapper = WrapperHoc(RiMessages);
const MaventaSettingsWrapper = WrapperHoc(MaventaSettings);
const WebhooksSettingsWrapper = WrapperHoc(WebhooksSettings);
const MeteredBillingPeriodsWrapper = WrapperHoc(MeteredBillingPeriods);
const MeteredBillingPeriodWrapper = WrapperHoc(MeteredBillingPeriod);
const MeteredBillingSubjectsWrapper = WrapperHoc(MeteredBillingSubjects);
const MeteredBillingSubjectFormWrapper = WrapperHoc(MeteredBillingSubjectForm);
const CompanySettingsWrapper = WrapperHoc(CompanySettings);
const TenantUsersWrapper = WrapperHoc(TenantUsers);
const RoleManagementWrapper = WrapperHoc(RoleManagement);
const ExportDataWrapper = WrapperHoc(ExportData);
const EmployeesWrapper = WrapperHoc(Employees);
const EmployeeFormWrapper = WrapperHoc(EmployeeForm);
const DepartmentsWrapper = WrapperHoc(Departments);
const DepartmentFormWrapper = WrapperHoc(DepartmentForm);
const ExpensesWrapper = WrapperHoc(ExpensesPage);
const ExpenseFormWrapper = WrapperHoc(ExpenseForm);
const ExpenseCategoriesWrapper = WrapperHoc(ExpenseCategories);
const ExpenseCategoryFormWrapper = WrapperHoc(ExpenseCategoryForm);
const BIDashboardWrapper = WrapperHoc(BIDashboard);
const ReportTemplatesWrapper = WrapperHoc(ReportRunner);
const ReportDesignerWrapper = WrapperHoc(ReportDesigner);
const ReportViewerWrapper = WrapperHoc(ReportViewer);
const OpenItemsDashboardWrapper = WrapperHoc(OpenItemsDashboard);
const OpenItemsWorkQueueWrapper = WrapperHoc(OpenItemsWorkQueue);
const BoardDashboardWrapper = WrapperHoc(BoardDashboard);

// Admin wrappers - Note: Admin pages don't need tenantId prop
const AdminDashboardWrapper = WrapperHoc(AdminDashboard);
const HostingCompaniesWrapper = WrapperHoc(HostingCompanies);
const HostingCompanyFormWrapper = WrapperHoc(HostingCompanyForm);
const TenantsWrapper = WrapperHoc(Tenants);
const TenantFormWrapper = WrapperHoc(TenantForm);
const UsersWrapper = WrapperHoc(Users);
const UserFormWrapper = WrapperHoc(UserForm);
const AccessLogsWrapper = WrapperHoc(AccessLogs);
const ActiveSessionsWrapper = WrapperHoc(ActiveSessions);
const BankingJobQueueWrapper = WrapperHoc(BankingJobQueue);
const DatabaseImportWrapper = WrapperHoc(DatabaseImport);
const VoucherCorrectionWrapper = WrapperHoc(VoucherCorrection);
const PaymentRematchWrapper = WrapperHoc(PaymentRematch);
const CloseRemainderWrapper = WrapperHoc(CloseRemainder);
const ChargeApCorrectionWrapper = WrapperHoc(ChargeApCorrection);
const CoaRoleCorrectionWrapper = WrapperHoc(CoaRoleCorrection);
const UserSettingsWrapper = WrapperHoc(UserSettings);


function TenantStoreProvider({ children }) {
  const { currentTenant } = useTenant();
  return <StoreProvider tenantId={currentTenant?.id}>{children}</StoreProvider>;
}

function TenantSupportProvider({ children }) {
  const { currentTenant } = useTenant();
  return <SupportProvider tenantId={currentTenant?.id}>{children}</SupportProvider>;
}

function AppContent() {
  const { theme } = useThemeMode();
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <TenantProvider>
            <BreadcrumbProvider>
              <ChatProvider>
              <ModuleAccessProvider>
              <TenantStoreProvider>
              <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <TenantSupportProvider>
                <DirtyFormProvider>
                <NavigationBlocker />
                <StoreRegistrar />
                <Routes>
                  {/* Root redirect - goes to dashboard if signed in, login if not */}
                  <Route path="/" element={<RootRedirect />} />

                  {/* Public Auth Routes */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/app/login" element={<Login />} />
                  <Route path="/register" element={<Login />} />
                  <Route path="/app/register" element={<Login />} />

                  {/* OAuth Callback Routes */}
                  <Route path="/auth/google/callback" element={<GoogleCallback />} />
                  <Route path="/auth/azure/callback" element={<AzureCallback />} />
                  {/* Enable Banking sends the customer back to one pre-registered URL, so this
                      route has no tenant slug; the page resolves the tenant from `state`. */}
                  <Route
                    path="/bank/enable-banking/callback"
                    element={
                      <PrivateRoute requireSetup={false}>
                        <EnableBankingCallbackPage />
                      </PrivateRoute>
                    }
                  />

                  {/* Public Invoice Viewer (accessed via signed link from email) */}
                  <Route path="/invoice-view" element={<PublicInvoiceViewer />} />

                  {/* Customer support portal (VII-207). Has its own magic-link auth,
                      runs entirely outside the main /app/ tenant chrome. */}
                  <Route path="/portal/*" element={<PortalRoutes />} />

                  {/* Private Routes — tenant slug lives in the URL as /app/{tenantSlug}/...
                      (VII-270). Legacy ?tenant=<uuid> bookmarks are caught by TenantSlugGuard
                      and rewritten in place to the canonical slug form. */}
                  <Route
                    path="/app"
                    element={
                      <PrivateRoute>
                        <Layout />
                      </PrivateRoute>
                    }
                  >
                    <Route index element={<RootSlugRedirect />} />
                  </Route>
                  <Route
                    path="/app/:tenantSlug"
                    element={
                      <PrivateRoute>
                        <TenantSlugGuard>
                          <Layout />
                        </TenantSlugGuard>
                      </PrivateRoute>
                    }
                  >
                    <Route index element={<Navigate to="dashboard" replace />} />
                    <Route path="dashboard" element={<DashboardWrapper />} />
                    <Route path="activity" element={<ActivityWrapper />} />
                    {/* Open items hub — overview + work queue. All-companies triage lives on My Desktop. */}
                    <Route path="open-items/all-companies" element={<Navigate to="/me/all-companies" replace />} />
                    <Route path="open-items/work-queue" element={<OpenItemsWorkQueueWrapper />} />
                    <Route path="open-items" element={<OpenItemsDashboardWrapper />} />
                    <Route path="board" element={<BoardDashboardWrapper />} />
                    <Route path="invoicing" element={<ModuleDashboardWrapper moduleKey="invoicing" />} />
                    <Route path="invoices" element={<InvoicesWrapper />} />
                    <Route path="invoices/new" element={<InvoiceFormWrapper />} />
                    <Route path="invoices/:id" element={<InvoiceFormWrapper />} />
                    <Route path="transfer-invoices" element={<TransferInvoicesWrapper />} />
                    <Route path="bulk-invoicing" element={<BulkInvoicingWrapper />} />
                    <Route path="bulk-invoicing/by-product" element={<BulkInvoicingByProductWrapper />} />
                    <Route path="bulk-invoicing/water" element={<WaterBillingWrapper />} />
                    <Route path="bulk-invoicing/metered" element={<BulkInvoicingMeteredWrapper />} />
                    <Route path="contract-invoices" element={<ContractInvoicesWrapper />} />
                    <Route path="contract-invoices/new" element={<ContractInvoiceFormWrapper />} />
                    <Route path="contract-invoices/:id" element={<ContractInvoiceFormWrapper />} />
                    <Route path="invoice-reports" element={<InvoiceReportsWrapper />} />
                    <Route path="invoice-report" element={<InvoiceListReportWrapper />} />
                    <Route path="payment-notifications" element={<PaymentNotificationsWrapper />} />
                    <Route path="sales-orders" element={<SalesOrdersWrapper />} />
                    <Route path="sales-orders/new" element={<SalesOrderFormWrapper />} />
                    <Route path="sales-orders/:id" element={<SalesOrderFormWrapper />} />
                    <Route path="sales-reports" element={<SalesReportsWrapper />} />
                    <Route path="purchasing" element={<ModuleDashboardWrapper moduleKey="purchasing" />} />
                    <Route path="purchasing/settings" element={<PurchasingSettingsWrapper />} />
                    <Route path="purchasing/approval-rules" element={<PurchaseInvoiceApprovalRulesSettingsWrapper />} />
                    <Route path="purchasing/pending-approvals" element={<PendingApprovalsPageWrapper />} />
                    <Route path="purchase-invoices" element={<PurchaseInvoicesWrapper />} />
                    <Route path="purchase-invoices/new" element={<PurchaseInvoiceFormWrapper />} />
                    <Route path="purchase-invoices/:id" element={<PurchaseInvoiceFormWrapper />} />
                    <Route path="charges" element={<ChargesWrapper />} />
                    <Route path="charges/new" element={<ChargeFormWrapper />} />
                    <Route path="charges/:id" element={<ChargeFormWrapper />} />
                    <Route path="purchase-orders" element={<PurchaseOrdersWrapper />} />
                    <Route path="purchase-orders/new" element={<PurchaseOrderFormWrapper />} />
                    <Route path="purchase-orders/:id" element={<PurchaseOrderFormWrapper />} />
                    <Route path="purchasing-reports" element={<PurchasingReportsWrapper />} />
                    <Route path="crm" element={<ModuleDashboardWrapper moduleKey="crm" />} />
                    <Route path="contacts" element={<ContactsWrapper />} />
                    <Route path="contacts/new" element={<ContactFormWrapper />} />
                    <Route path="contacts/:id" element={<ContactFormWrapper />} />
                    <Route path="contact-groups" element={<ContactGroupsWrapper />} />
                    <Route path="contact-groups/new" element={<ContactGroupFormWrapper />} />
                    <Route path="contact-groups/:id" element={<ContactGroupFormWrapper />} />
                    <Route path="activities" element={<ActivitiesWrapper />} />
                    <Route path="activities/new" element={<ActivityFormWrapper />} />
                    <Route path="activities/:id" element={<ActivityFormWrapper />} />
                    <Route path="leads" element={<LeadsWrapper />} />
                    <Route path="leads/new" element={<LeadFormWrapper />} />
                    <Route path="leads/:id" element={<LeadFormWrapper />} />
                    <Route path="deals" element={<DealsWrapper />} />
                    <Route path="deals/new" element={<DealFormWrapper />} />
                    <Route path="deals/:id" element={<DealFormWrapper />} />
                    <Route path="crm-reports" element={<CrmReportsWrapper />} />
                    <Route path="crm/settings/pipeline" element={<PipelineSettingsWrapper />} />
                    <Route path="inventory" element={<ModuleDashboardWrapper moduleKey="inventory" />} />
                    <Route path="products" element={<ProductsWrapper />} />
                    <Route path="products/new" element={<ProductFormWrapper />} />
                    <Route path="products/:id" element={<ProductFormWrapper />} />
                    <Route path="stocks" element={<StocksWrapper />} />
                    <Route path="stocks/new" element={<StockFormWrapper />} />
                    <Route path="stocks/:id" element={<StockFormWrapper />} />
                    <Route path="units-configuration" element={<UnitsConfigurationWrapper />} />
                    <Route path="units/new" element={<UnitFormWrapper />} />
                    <Route path="units/:id" element={<UnitFormWrapper />} />
                    <Route path="product-groups" element={<ProductGroupsWrapper />} />
                    <Route path="product-groups/new" element={<ProductGroupFormWrapper />} />
                    <Route path="product-groups/:id" element={<ProductGroupFormWrapper />} />
                    <Route path="inventory-reports" element={<InventoryReportsWrapper />} />
                    <Route path="accounting" element={<ModuleDashboardWrapper moduleKey="accounting" />} />
                    <Route path="payments" element={<PaymentsWrapper />} />
                    <Route path="payments/new" element={<PaymentFormWrapper />} />
                    <Route path="payments/:id" element={<PaymentFormWrapper />} />
                    <Route path="vouchers" element={<VouchersWrapper />} />
                    <Route path="talousarvio" element={<TalousarvioPageWrapper />} />
                    <Route path="ledger-browser" element={<LedgerBrowserWrapper />} />
                    <Route path="account-matching" element={<AccountMatchingWrapper />} />
                    <Route path="vouchers/new" element={<VoucherFormWrapper />} />
                    <Route path="vouchers/:id" element={<VoucherFormWrapper />} />
                    <Route path="vat-configuration" element={<VatConfigurationWrapper />} />
                    <Route path="vat/new" element={<VatFormWrapper />} />
                    <Route path="vat/:id" element={<VatFormWrapper />} />
                    <Route path="coa-configuration" element={<ChartOfAccountsConfigurationWrapper />} />
                    <Route path="coa/new" element={<ChartOfAccountsFormWrapper />} />
                    <Route path="coa/:id" element={<ChartOfAccountsFormWrapper />} />
                    <Route path="financial-years-configuration" element={<FinancialYearsConfigurationWrapper />} />
                    <Route path="financial-years/new" element={<FinancialYearFormWrapper />} />
                    <Route path="financial-years/:id" element={<FinancialYearFormWrapper />} />
                    <Route path="voucher-types-configuration" element={<VoucherTypesConfigurationWrapper />} />
                    <Route path="voucher-types/new" element={<VoucherTypeFormWrapper />} />
                    <Route path="voucher-types/:id" element={<VoucherTypeFormWrapper />} />
                    <Route path="entry-types-configuration" element={<EntryTypesConfigurationWrapper />} />
                    <Route path="dimensions-configuration" element={<DimensionsConfigurationWrapper />} />
                    <Route path="charge-financing-balances" element={<ChargeFinancingBalancesWrapper />} />
                    <Route path="dimensions/new" element={<DimensionFormWrapper />} />
                    <Route path="dimensions/:id" element={<DimensionFormWrapper />} />
                    <Route path="currencies-configuration" element={<CurrenciesConfigurationWrapper />} />
                    <Route path="exchange-rates" element={<ExchangeRatesWrapper />} />
                    <Route path="exchange-rates/:currency" element={<ExchangeRateHistoryWrapper />} />
                    <Route path="currencies/new" element={<CurrencyFormWrapper />} />
                    <Route path="currencies/:id" element={<CurrencyFormWrapper />} />
                    <Route path="coa-role-configs-configuration" element={<CoaRoleConfigsConfigurationWrapper />} />
                    <Route path="coa-role-config/new" element={<CoaRoleConfigFormWrapper />} />
                    <Route path="coa-role-config/:id" element={<CoaRoleConfigFormWrapper />} />
                    <Route path="coa-groups-configuration" element={<CoaGroupsConfigurationWrapper />} />
                    <Route path="coa-groups/new" element={<CoaGroupFormWrapper />} />
                    <Route path="coa-groups/:id" element={<CoaGroupFormWrapper />} />
                    <Route path="accounting-reports" element={<AccountingReportsWrapper />} />
                    <Route path="payment-terms-configuration" element={<PaymentTermsConfigurationWrapper />} />
                    <Route path="payment-terms/new" element={<PaymentTermFormWrapper key="new" />} />
                    <Route path="payment-terms/:id" element={<PaymentTermFormWrapper key="edit" />} />
                    <Route path="payment-methods-configuration" element={<PaymentMethodsConfigurationWrapper />} />
                    <Route path="payment-methods/new" element={<PaymentMethodFormWrapper />} />
                    <Route path="payment-methods/:id" element={<PaymentMethodFormWrapper />} />
                    <Route path="settings" element={<UserSettingsWrapper />} />
                    <Route path="sending-service" element={<SendingServiceWrapper />} />
                    <Route path="settings/si-messages" element={<SiMessagesWrapper />} />
                    <Route path="settings/ri-messages" element={<RiMessagesWrapper />} />
                    <Route path="settings/maventa" element={<MaventaSettingsWrapper />} />
                    <Route path="settings/webhooks" element={<WebhooksSettingsWrapper />} />
                    <Route path="metered-billing/periods" element={<MeteredBillingPeriodsWrapper />} />
                    <Route path="metered-billing/periods/:periodId" element={<MeteredBillingPeriodWrapper />} />
                    <Route path="metered-billing/subjects" element={<MeteredBillingSubjectsWrapper />} />
                    <Route path="metered-billing/subjects/new" element={<MeteredBillingSubjectFormWrapper />} />
                    <Route path="metered-billing/subjects/:subjectId" element={<MeteredBillingSubjectFormWrapper />} />
                    <Route path="bank" element={<ModuleDashboardWrapper moduleKey="banking" />} />
                    <Route path="bank/payments/rebuild/:customerId" element={<CustomerAllocationRebuildWrapper />} />
                    <Route path="bank/payments/overview" element={<AllocationOverviewWrapper />} />
                    <Route path="bank/payments" element={<BankPaymentsWrapper />} />
                    <Route path="bank/statements" element={<StatementsWrapper />} />
                    <Route path="bank/statements/:id" element={<StatementsWrapper />} />
                    <Route path="bank/enable-banking" element={<EnableBankingConnectionsWrapper />} />
                    <Route path="bank/enable-banking/:id" element={<EnableBankingConnectionWrapper />} />
                    <Route path="salaries" element={<SalariesDashboardWrapper />} />
                    <Route path="salaries/workers" element={<EmployeesWrapper />} />
                    <Route path="salaries/accounting" element={<SalaryAccountingImportWrapper />} />
                    <Route path="salaries/payments" element={<SalaryPaymentsWrapper />} />
                    <Route path="salaries/:section" element={<SalariesSectionWrapper />} />
                    <Route path="helpdesk/manuals" element={<ManualsWrapper />} />
                    <Route path="helpdesk/manuals/new" element={<ManualEditorWrapper />} />
                    <Route path="helpdesk/manuals/:id" element={<ManualEditorWrapper />} />
                    <Route path="knowledgebase" element={<KnowledgebaseListWrapper />} />
                    <Route path="knowledgebase/entries/new" element={<KnowledgebaseEditorWrapper mode="new" />} />
                    <Route path="knowledgebase/entries/:id" element={<KnowledgebaseEntryWrapper />} />
                    <Route path="knowledgebase/entries/:id/edit" element={<KnowledgebaseEditorWrapper mode="edit" />} />
                    <Route path="time-tracking" element={<TimeTrackerWrapper />} />
                    <Route path="time-tracking/all-entries" element={<AllTimeEntriesWrapper />} />
                    <Route path="time-tracking/week" element={<WeekCalendarWrapper />} />
                    {/* Projects module: My tasks at /app/tasks (main); per-project tasks under /app/projects/.../tasks */}
                    <Route path="tasks" element={<MyTasksWrapper />} />
                    <Route path="projects" element={<ProjectsWrapper />} />
                    <Route path="projects/new" element={<ProjectFormWrapper />} />
                    <Route path="projects/:id" element={<ProjectFormWrapper />} />
                    <Route path="projects/:projectId/tasks" element={<TasksWrapper />} />
                    <Route path="projects/stages-matrix" element={<TaskStagesMatrixWrapper />} />
                    <Route path="projects/stage-catalog" element={<TaskStagesMatrixWrapper />} />
                    <Route path="projects/tags" element={<TagsWrapper />} />
                    <Route path="project-templates" element={<ProjectTemplatesWrapper />} />
                    <Route path="tasks/new" element={<TaskFormWrapper />} />
                    <Route path="projects/:projectId/tasks/new" element={<TaskFormWrapper />} />
                    <Route path="projects/:projectId/tasks/:taskId" element={<TaskFormWrapper />} />
                    <Route path="company-settings" element={<CompanySettingsWrapper />} />
                    <Route path="tenant-users" element={<TenantUsersWrapper />} />
                    <Route path="role-management" element={<RoleManagementWrapper />} />
                    <Route path="user-groups" element={<UserGroupsWrapper />} />
                    <Route path="export-data" element={<ExportDataWrapper />} />
                    <Route path="employees" element={<EmployeesWrapper />} />
                    <Route path="employees/new" element={<EmployeeFormWrapper />} />
                    <Route path="employees/:id" element={<EmployeeFormWrapper />} />
                    <Route path="departments" element={<DepartmentsWrapper />} />
                    <Route path="departments/new" element={<DepartmentFormWrapper />} />
                    <Route path="departments/:id" element={<DepartmentFormWrapper />} />
                    <Route path="expenses" element={<ExpensesWrapper />} />
                    <Route path="expenses/new" element={<ExpenseFormWrapper />} />
                    <Route path="expenses/:id" element={<ExpenseFormWrapper />} />
                    <Route path="expense-categories" element={<ExpenseCategoriesWrapper />} />
                    <Route path="expense-categories/new" element={<ExpenseCategoryFormWrapper />} />
                    <Route path="expense-categories/:id" element={<ExpenseCategoryFormWrapper />} />

                    {/* Business Intelligence Routes */}
                    <Route path="bi" element={<BIDashboardWrapper />} />
                    <Route path="bi/:id" element={<BIDashboardWrapper />} />

                    {/* Reporting Routes */}
                    <Route path="report-templates" element={<ReportTemplatesWrapper />} />
                    <Route path="report-designer" element={<ReportDesignerWrapper />} />
                    <Route path="report-designer/:id" element={<ReportDesignerWrapper />} />
                    <Route path="report-viewer/:id" element={<ReportViewerWrapper />} />

                    {/* Admin Routes */}
                    <Route path="admin" element={<AdminDashboardWrapper />} />
                    <Route path="admin/hosts" element={<HostingCompaniesWrapper />} />
                    <Route path="admin/hosts/new" element={<HostingCompanyFormWrapper />} />
                    <Route path="admin/hosts/:id" element={<HostingCompanyFormWrapper />} />
                    <Route path="admin/tenants" element={<TenantsWrapper />} />
                    <Route path="admin/tenants/new" element={<TenantFormWrapper />} />
                    <Route path="admin/tenants/:id" element={<TenantFormWrapper />} />
                    <Route path="admin/users" element={<UsersWrapper />} />
                    <Route path="admin/users/new" element={<UserFormWrapper />} />
                    <Route path="admin/users/:id" element={<UserFormWrapper />} />
                    <Route path="admin/access-logs" element={<AccessLogsWrapper />} />
                    <Route path="admin/sessions" element={<ActiveSessionsWrapper />} />
                    <Route path="admin/banking/job-queue" element={<BankingJobQueueWrapper />} />
                    <Route path="admin/database-import" element={<DatabaseImportWrapper />} />
                    <Route path="admin/voucher-correction" element={<VoucherCorrectionWrapper />} />
                    <Route path="admin/voucher-correction/:hostId" element={<VoucherCorrectionWrapper />} />
                    <Route path="admin/voucher-correction/:hostId/compare" element={<VoucherCorrectionWrapper />} />
                    <Route path="admin/voucher-correction/:hostId/:tenantId" element={<VoucherCorrectionWrapper />} />
                    <Route path="admin/voucher-correction/:hostId/:tenantId/compare" element={<VoucherCorrectionWrapper />} />
                    <Route path="admin/payment-rematch" element={<PaymentRematchWrapper />} />
                    <Route path="admin/payment-rematch/:hostId" element={<PaymentRematchWrapper />} />
                    <Route path="admin/payment-rematch/:hostId/:tenantId" element={<PaymentRematchWrapper />} />
                    <Route path="admin/close-remainder" element={<CloseRemainderWrapper />} />
                    <Route path="admin/close-remainder/:hostId" element={<CloseRemainderWrapper />} />
                    <Route path="admin/close-remainder/:hostId/:tenantId" element={<CloseRemainderWrapper />} />
                    <Route path="admin/charge-ap-correction" element={<ChargeApCorrectionWrapper />} />
                    <Route path="admin/charge-ap-correction/:hostId" element={<ChargeApCorrectionWrapper />} />
                    <Route path="admin/charge-ap-correction/:hostId/:tenantId" element={<ChargeApCorrectionWrapper />} />
                    <Route path="admin/coa-role-correction" element={<CoaRoleCorrectionWrapper />} />
                    <Route path="admin/coa-role-correction/:hostId" element={<CoaRoleCorrectionWrapper />} />
                    <Route path="admin/coa-role-correction/:hostId/:tenantId" element={<CoaRoleCorrectionWrapper />} />
                    {/* Catch-all inside the app shell — broken links render
                        a 404 in the content area instead of falling through
                        to the outer redirect (which would land on dashboard). */}
                    <Route path="*" element={<NotFoundWrapper />} />
                  </Route>

                  {/* My Desktop (/me/*) — dedicated MeLayout (host picker, no tenant shop gates).
                      Cross-tenant, so the current tenant's company-info setup gate does not apply. */}
                  <Route
                    path="/me"
                    element={
                      <PrivateRoute requireSetup={false}>
                        <ViiluCommonPackageProviders>
                          <MeLayout />
                        </ViiluCommonPackageProviders>
                      </PrivateRoute>
                    }
                  >
                    <Route index element={<MyHome />} />
                    <Route path="all-companies" element={<MeAllCompanies />} />
                    <Route path="approvals" element={<MyApprovals />} />
                    <Route path="tasks" element={<MyTasksAcrossTenants />} />
                    <Route path="companies" element={<MyCompanies />} />
                  </Route>

                  {/* Catch-all route - redirect to root which handles auth check */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </DirtyFormProvider>
                </TenantSupportProvider>
              </Router>
              </TenantStoreProvider>
              </ModuleAccessProvider>
              </ChatProvider>
            </BreadcrumbProvider>
          </TenantProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <ThemeModeProvider>
        <AppContent />
      </ThemeModeProvider>
    </I18nextProvider>
  );
}

export default App;
